'use client';

import { useEffect, useState, useMemo, useRef } from 'react';
import { useCartStore, type CartItem, type CartCoupon } from '@/lib/cartStore';
import { trackSharedCartLinkOpened } from '@/lib/linkTracking';
import { useParams, useRouter, useSearchParams } from 'next/navigation';
import { ProductMedia } from '@/components/product-media';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Minus, Plus, Trash2, ShoppingBag, Tag } from 'lucide-react';
import { toast } from 'sonner';
import { getStoreClosedReason } from '@/lib/operatingHours';
import { useTranslations } from 'next-intl';
import { formatCurrency } from '@/lib/utils';
import {
  getDisplayVariantDetails,
  resolveVariantAttributeLabel,
} from '@/lib/variantDetails';
import { FoodSelectionsDisplay } from '@/components/food-selections-display';
import { calculateCouponDiscount } from '@/lib/couponCalculator';
import { CartItemPrice } from '@/components/cart-item-price';
import { calculateCartItemPriceSync, getCartLineUnitPrices, type CartProductPricing } from '@/lib/cartPriceCalculator';
import { Spinner } from '@/components/ui/spinner';
import { Badge } from '@/components/ui/badge';
import {
  getCartLineIssue,
  type CartProductSnap,
} from '@/lib/cartAvailability';
import { CartRelatedProducts } from '@/components/cart-related-products';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

function cartItemKey(item: CartItem, index: number): string {
  if (item.variantId) {
    return `${item.productId}-${item.variantId}`;
  }
  if (item.foodSelections) {
    return `${item.productId}-${JSON.stringify(item.foodSelections)}`;
  }
  return `${item.productId}-${index}`;
}

export default function CartPage() {
  const t = useTranslations();
  const params = useParams();
  const router = useRouter();
  const storeId = params.storeId as string;
  const locale = params.locale as string;

  const searchParams = useSearchParams();
  const cartIdFromUrl = searchParams.get('cart');
  const intentIdFromUrl = searchParams.get('intent');
  const trackingRefFromUrl = searchParams.get('ref');

  const items = useCartStore((state) => state.items);
  const updateQuantity = useCartStore((state) => state.updateQuantity);
  const removeItem = useCartStore((state) => state.removeItem);
  const clearCart = useCartStore((state) => state.clearCart);
  const getTotal = useCartStore((state) => state.getTotal);
  const setStoreId = useCartStore((state) => state.setStoreId);
  const replaceCart = useCartStore((state) => state.replaceCart);
  const setCoupon = useCartStore((state) => state.setCoupon);
  const coupon = useCartStore((state) => state.coupon);
  const clearCoupon = useCartStore((state) => state.clearCoupon);
  const setCheckoutIntentId = useCartStore((state) => state.setCheckoutIntentId);
  const [loadingSharedCart, setLoadingSharedCart] = useState(!!cartIdFromUrl || !!intentIdFromUrl);
  const [loadedSharedCart, setLoadedSharedCart] = useState<{ storeId: string; itemCount: number } | null>(null);
  const trackedRefRef = useRef<string | null>(null);
  const [isResolvingStore, setIsResolvingStore] = useState(true);
  const [resolvedStoreId, setResolvedStoreId] = useState<string>('');
  const [productDataCache, setProductDataCache] = useState<Record<string, CartProductPricing>>({});
  const [productSnapshots, setProductSnapshots] = useState<
    Record<string, CartProductSnap | null>
  >({});
  const [showClearCartDialog, setShowClearCartDialog] = useState(false);
  // Products that have an active recovery rule: a stock-short line for one of these can proceed
  // to checkout (where the substitution dialog is offered) instead of being hard-blocked here.
  const [recoverySourceIds, setRecoverySourceIds] = useState<Set<string>>(new Set());

  const itemsRef = useRef(items);
  itemsRef.current = items;

  /** Unique product IDs plus which of those need food pricing (refetch when a food line is added). */
  const cartProductFetchKey = useMemo(() => {
    const ids = [...new Set(items.map((i) => i.productId))].sort().join(',');
    const foodIds = [
      ...new Set(
        items.filter((i) => i.foodSelections).map((i) => i.productId),
      ),
    ].sort().join(',');
    return `${ids}__${foodIds}`;
  }, [items]);

  const availabilityReady = useMemo(() => {
    const ids = [...new Set(items.map((i) => i.productId))];
    if (ids.length === 0) return true;
    return ids.every((id) =>
      Object.prototype.hasOwnProperty.call(productSnapshots, id),
    );
  }, [items, productSnapshots]);

  const lineIssuesByKey = useMemo(() => {
    const map: Record<string, ReturnType<typeof getCartLineIssue>> = {};
    if (!availabilityReady) return map;
    items.forEach((item, index) => {
      const key = cartItemKey(item, index);
      const snap = productSnapshots[item.productId] ?? null;
      map[key] = getCartLineIssue(item, snap, t);
    });
    return map;
  }, [items, productSnapshots, availabilityReady, t]);

  const disableCheckout =
    !availabilityReady ||
    items.some((item, index) => {
      const issue = lineIssuesByKey[cartItemKey(item, index)];
      if (!issue || issue.ok) return false;
      // A stock shortage on a product with a recovery rule can proceed — the checkout offers
      // the substitution. Removed/inactive products still block.
      if (issue.recoverable && recoverySourceIds.has(item.productId)) return false;
      return true;
    });

  // Load which cart products have a recovery rule (to allow proceeding past a stock shortage).
  useEffect(() => {
    if (!resolvedStoreId) return;
    let cancelled = false;
    fetch(`/api/stores/${resolvedStoreId}/recovery-sources`)
      .then((res) => (res.ok ? res.json() : { sourceProductIds: [] }))
      .then((data: { sourceProductIds?: string[] }) => {
        if (!cancelled) setRecoverySourceIds(new Set(data.sourceProductIds ?? []));
      })
      .catch(() => {
        /* best-effort: without this, short lines simply block as before */
      });
    return () => {
      cancelled = true;
    };
  }, [resolvedStoreId]);

  // Load shared cart from ?cart=id, abandoned-checkout intent from ?intent=id, or initialize store cart
  useEffect(() => {
    if (!storeId) return;

    if (intentIdFromUrl) {
      setLoadingSharedCart(true);
      fetch(`/api/stores/${storeId}/checkout-intents/${intentIdFromUrl}`)
        .then((res) => {
          if (!res.ok) throw new Error('Checkout intent not found');
          return res.json();
        })
        .then((payload: { data: { storeId: string; items: Array<{ productId: string; variantId?: string; name: string; price: number; quantity: number }> } }) => {
          const intentItems: CartItem[] = (payload.data.items ?? []).map((item) => ({
            productId: item.productId,
            variantId: item.variantId,
            name: item.name,
            price: item.price,
            quantity: item.quantity,
          }));
          replaceCart(payload.data.storeId, intentItems);
          setCheckoutIntentId(intentIdFromUrl);
          clearCoupon();
        })
        .catch(() => {
          setStoreId(storeId);
        })
        .finally(() => setLoadingSharedCart(false));
      return;
    }

    if (cartIdFromUrl) {
      setLoadingSharedCart(true);
      fetch(`/api/cart/${cartIdFromUrl}`)
        .then((res) => {
          if (!res.ok) throw new Error('Cart not found');
          return res.json();
        })
        .then((data: { storeId: string; items: Array<{ productId: string; variantId?: string; name: string; price: number; quantity: number; imageUrl?: string; variantDetails?: Record<string, string> }>; prefillPhone?: string | null; coupon?: CartCoupon | null }) => {
          const cartItems: CartItem[] = (data.items ?? []).map((item) => ({
            productId: item.productId,
            variantId: item.variantId,
            name: item.name,
            price: item.price,
            quantity: item.quantity,
            imageUrl: item.imageUrl,
            variantDetails: item.variantDetails,
          }));
          replaceCart(data.storeId, cartItems, cartIdFromUrl, data.prefillPhone ?? null);
          setLoadedSharedCart({ storeId: data.storeId, itemCount: cartItems.length });
          // Staff may pre-select a coupon when sharing the cart; apply it so the
          // recipient sees the discount already active. Otherwise clear any stale one.
          if (data.coupon) {
            setCoupon(data.coupon);
          } else {
            clearCoupon();
          }
        })
        .catch(() => {
          setStoreId(storeId);
        })
        .finally(() => setLoadingSharedCart(false));
    } else {
      setStoreId(storeId);
    }
  }, [storeId, cartIdFromUrl, intentIdFromUrl, replaceCart, setStoreId, setCoupon, clearCoupon, setCheckoutIntentId]);

  // Carts sent from the Ao Vivo board carry the conversation token; track once, with the store UUID.
  useEffect(() => {
    if (!trackingRefFromUrl || !loadedSharedCart || trackedRefRef.current === trackingRefFromUrl) return;
    trackedRefRef.current = trackingRefFromUrl;
    trackSharedCartLinkOpened(loadedSharedCart.storeId, trackingRefFromUrl, loadedSharedCart.itemCount > 0);
  }, [trackingRefFromUrl, loadedSharedCart]);

  // Load catalog snapshots for cart lines (stock, active) and food pricing data
  useEffect(() => {
    const [idsPart] = cartProductFetchKey.split('__');
    const uniqueIds = (idsPart ?? '').split(',').filter(Boolean);
    if (uniqueIds.length === 0) {
      return;
    }

    let cancelled = false;

    void (async () => {
      await Promise.all(
        uniqueIds.map(async (productId) => {
          try {
            const response = await fetch(
              `/api/products/${productId}?forCustomerCatalog=1`
            );
            if (cancelled) return;
            if (!response.ok) {
              setProductSnapshots((prev) => ({ ...prev, [productId]: null }));
              return;
            }
            const product = await response.json();
            if (cancelled) return;

            const snap: CartProductSnap = {
              active: product.active !== false,
              unlimitedStock: product.unlimitedStock === true,
              stock: Number(product.stock) || 0,
              variants: (product.variants || []).map(
                (v: { id: string; stock: unknown }) => ({
                  id: v.id,
                  stock:
                    typeof v.stock === 'number'
                      ? v.stock
                      : Number(v.stock) || 0,
                }),
              ),
            };
            setProductSnapshots((prev) => ({ ...prev, [productId]: snap }));

            const listPrice = Number(product.price) || 0;
            const promotionPrice =
              product.promotionPrice && product.promotionPrice > 0
                ? Number(product.promotionPrice)
                : null;

            setProductDataCache((prev) => ({
              ...prev,
              [productId]: {
                optionGroups: product.optionGroups || [],
                additionals: product.additionals || [],
                price: listPrice,
                promotionPrice,
                basePrice: promotionPrice ?? listPrice,
              },
            }));
          } catch {
            if (!cancelled) {
              setProductSnapshots((prev) => ({ ...prev, [productId]: null }));
            }
          }
        }),
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [cartProductFetchKey]);

  // Calculate item prices including options and additionals
  const itemPrices = useMemo(() => {
    const prices: Record<string, number> = {};
    items.forEach((item, index) => {
      const key = cartItemKey(item, index);

      if (item.foodSelections && productDataCache[item.productId]) {
        prices[key] = calculateCartItemPriceSync(item, productDataCache[item.productId]);
      } else {
        prices[key] = Number(item.price) || 0;
      }
    });
    return prices;
  }, [items, productDataCache]);

  const itemPriceDetails = useMemo(() => {
    const details: Record<string, ReturnType<typeof getCartLineUnitPrices>> = {};
    items.forEach((item, index) => {
      const key = cartItemKey(item, index);
      const productData = productDataCache[item.productId];
      const effectiveFromCalc = itemPrices[key] ?? (Number(item.price) || 0);
      const linePrices = getCartLineUnitPrices(item, productData);
      details[key] = {
        ...linePrices,
        effective: item.foodSelections ? linePrices.effective : effectiveFromCalc,
      };
    });
    return details;
  }, [items, itemPrices, productDataCache]);

  // Calculate subtotal using recalculated prices
  const subtotal = useMemo(() => {
    return items.reduce((sum, item, index) => {
      const key = cartItemKey(item, index);
      const price = Number(itemPrices[key] || item.price) || 0;
      const quantity = Number(item.quantity) || 0;
      return sum + (price * quantity);
    }, 0);
  }, [items, itemPrices]);


  // Coupon discount preview. Delivery fee is unknown on the cart page (computed at
  // payment), so DELIVERY_FEE coupons show no reduction here and only apply at checkout.
  const couponDiscount = useMemo(() => {
    if (!coupon) return 0;
    const productSubtotal = coupon.productId
      ? items.reduce((sum, item, index) => {
          if (item.productId !== coupon.productId) return sum;
          const key = cartItemKey(item, index);
          const price = Number(itemPrices[key] || item.price) || 0;
          return sum + price * (Number(item.quantity) || 0);
        }, 0)
      : 0;
    const result = calculateCouponDiscount({ coupon, subtotal, deliveryFee: 0, productSubtotal });
    return result.subtotalDiscountAmount;
  }, [coupon, items, itemPrices, subtotal]);

  const total = Math.max(0, subtotal - couponDiscount);

  const handleUpdateQuantity = (productId: string, newQuantity: number, variantId?: string, foodSelections?: CartItem['foodSelections']) => {
    if (newQuantity < 1) return;
    updateQuantity(productId, newQuantity, variantId, foodSelections);
  };

  const handleRemoveItem = (productId: string, productName: string, variantId?: string, foodSelections?: CartItem['foodSelections']) => {
    removeItem(productId, variantId, foodSelections);
    toast.success(t('checkout.remove'), {
      description: productName,
    });
  };

  useEffect(() => {
    if (!storeId) {
      setIsResolvingStore(false);
      return;
    }

    let isMounted = true;

    const fetchStoreForHours = async () => {
      setIsResolvingStore(true);
      try {
        const response = await fetch(`/api/stores/${storeId}/checkout`);
        if (!response.ok) {
          throw new Error('Failed to fetch store');
        }

        const store = await response.json();
        if (!isMounted) {
          return;
        }

        if (store?.id) {
          setResolvedStoreId(store.id);
          setStoreId(store.id);
        }

        const closedReason = getStoreClosedReason(store);
        if (closedReason && isMounted) {
          toast.error(
            closedReason === 'temporarilyClosed'
              ? t('checkout.storeTemporarilyClosedRedirect')
              : t('checkout.storeClosedRedirect')
          );
          router.replace(`/${locale}/${storeId}/catalog`);
        }
      } catch (error) {
        console.error('[Cart] Failed to load store', error);
        if (isMounted) {
          toast.error(t('errors.loadingFailed'));
        }
      } finally {
        if (isMounted) {
          setIsResolvingStore(false);
        }
      }
    };

    fetchStoreForHours();

    return () => {
      isMounted = false;
    };
  }, [storeId, locale, t, router, setStoreId]);

  if (loadingSharedCart || isResolvingStore) {
    return (
      <div className="container mx-auto max-w-7xl py-12 px-4 flex flex-col items-center justify-center min-h-[40vh]">
        <Spinner />
        <p className="text-muted-foreground mt-4">{t('checkout.cart')}</p>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="container mx-auto max-w-7xl py-12 px-4">
        <div className="flex flex-col items-center justify-center space-y-6 py-12">
          <ShoppingBag className="w-24 h-24 text-muted-foreground" />
          <h1 className="text-base font-medium leading-snug">
            {t('checkout.emptyCart')}
          </h1>
          <p className="text-sm text-muted-foreground text-center max-w-md">
            {t('checkout.continueShopping')}
          </p>
          <Link href={`/${locale}/${storeId}/catalog`}>
            <Button className="mt-4">
              {t('catalog.browseProducts')}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4">
        <h1 className="text-base font-medium leading-snug mb-2">
          {t('checkout.cart')}
        </h1>
        <p className="text-sm text-muted-foreground">
          {items.length} {items.length === 1 ? t('checkout.item') : t('checkout.items')}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Cart Items */}
        <div className="lg:col-span-2 space-y-8 lg:space-y-4">
          {items.map((item, index) => {
            const itemKey = cartItemKey(item, index);
            const lineIssue = lineIssuesByKey[itemKey];
            const lineError =
              lineIssue && !lineIssue.ok ? lineIssue : null;
            const unitPrices = itemPriceDetails[itemKey] ?? getCartLineUnitPrices(item);
            const lineSubtotal = unitPrices.effective * item.quantity;
            const lineOriginalSubtotal = unitPrices.original * item.quantity;

            return (
              <Card key={itemKey} className="p-0">
                <CardContent className="p-4">
                  {/* Image and Product Info */}
                  <div className="flex gap-4 mb-2">
                    <div className="relative w-16 h-16 flex-shrink-0 bg-muted rounded-4xl overflow-hidden">
                      {item.imageUrl && (
                        <ProductMedia
                          src={item.imageUrl}
                          alt={item.name}
                          fill
                          sizes="64px"
                          className="object-cover"
                          autoPlay
                          loop
                        />
                      )}
                    </div>

                    <div className="flex flex-1 min-w-0 flex-col gap-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-sm font-medium leading-snug">
                          {item.name}
                        </h3>
                        {lineError ? (
                          <Badge variant="destructive" className="shrink-0">
                            {lineError.message}
                          </Badge>
                        ) : null}
                      </div>

                      <CartItemPrice
                        effective={unitPrices.effective}
                        original={unitPrices.original}
                        hasPromotion={unitPrices.hasPromotion}
                        discountPercent={unitPrices.discountPercent}
                        locale={locale}
                      />
                    </div>
                  </div>

                  {/* Variant Details */}
                  {(() => {
                    const displayVariantDetails = getDisplayVariantDetails(item.variantDetails);
                    if (displayVariantDetails.length === 0) {
                      return null;
                    }

                    return (
                      <div className="flex flex-wrap gap-2 mb-2">
                        {displayVariantDetails.map(([key, value]) => (
                          <span
                            key={key}
                            className="text-xs px-2 py-1 bg-muted rounded-4xl text-muted-foreground"
                          >
                            {resolveVariantAttributeLabel(key, t, t.has)}: {value}
                          </span>
                        ))}
                      </div>
                    );
                  })()}

                  {/* Food Selections */}
                  {item.foodSelections && (
                    <FoodSelectionsDisplay
                      productId={item.productId}
                      selections={item.foodSelections}
                      locale={locale}
                    />
                  )}

                  <div className="flex items-center justify-between gap-4 p-2 px-4 rounded-4xl bg-primary/10">
                    <div className="flex shrink-0 items-center rounded-full">
                      <Button
                        variant="ghost"
                        size="icon-lg"
                        aria-label={
                          item.quantity <= 1
                            ? t('checkout.remove')
                            : t('checkout.decreaseQuantity')
                        }
                        onClick={() =>
                          item.quantity <= 1
                            ? handleRemoveItem(
                              item.productId,
                              item.name,
                              item.variantId,
                              item.foodSelections
                            )
                            : handleUpdateQuantity(
                              item.productId,
                              item.quantity - 1,
                              item.variantId,
                              item.foodSelections
                            )
                        }
                      >
                        {item.quantity <= 1 ? (
                          <Trash2 className="w-4 h-4" />
                        ) : (
                          <Minus className="w-4 h-4" />
                        )}
                      </Button>
                      <Input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) =>
                          handleUpdateQuantity(
                            item.productId,
                            parseInt(e.target.value) || 1,
                            item.variantId,
                            item.foodSelections
                          )
                        }
                        className="h-6 w-6 p-0 text-sm text-center border-0 bg-transparent shadow-none rounded-none transition-none outline-none focus-visible:border-0 focus-visible:ring-0 focus-visible:outline-none focus-visible:shadow-none [-moz-appearance:textfield] [&::-webkit-outer-spin-button]:[-webkit-appearance:none] [&::-webkit-inner-spin-button]:[-webkit-appearance:none]"
                      />
                      <Button
                        variant="ghost"
                        size="icon-lg"
                        aria-label={t('checkout.increaseQuantity')}
                        onClick={() =>
                          handleUpdateQuantity(
                            item.productId,
                            item.quantity + 1,
                            item.variantId,
                            item.foodSelections
                          )
                        }
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    <CartItemPrice
                      effective={lineSubtotal}
                      original={lineOriginalSubtotal}
                      hasPromotion={unitPrices.hasPromotion}
                      discountPercent={unitPrices.discountPercent}
                      locale={locale}
                      alignEnd
                    />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Order Summary */}
        <div className="lg:col-span-1">
          <Card className="sticky top-4">
            <CardHeader>
              <CardTitle className="leading-snug">{t('checkout.summary')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">{t('checkout.subtotal')}</span>
                <span className="text-base font-semibold text-foreground">{formatCurrency(subtotal, locale)}</span>
              </div>

              {coupon && couponDiscount > 0 && (
                <div className="flex justify-between items-center text-green-700 dark:text-green-400">
                  <span className="text-sm flex items-center gap-1">
                    <Tag className="h-3.5 w-3.5" />
                    {coupon.code}
                  </span>
                  <span className="text-base font-semibold">-{formatCurrency(couponDiscount, locale)}</span>
                </div>
              )}

              <div className="items-start">
                <div className="min-w-0">
                  <span className="text-sm text-muted-foreground block">{t('checkout.shipping')}</span>
                  <span className="text-xs text-muted-foreground block mt-0.5">
                    {t('checkout.shippingCalculatedAtPayment')}
                  </span>
                </div>
              </div>

              <div className="border-t border-border pt-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">{t('checkout.total')}</span>
                  <span className="text-base font-semibold text-foreground">{formatCurrency(total, locale)}</span>
                </div>
              </div>

              {disableCheckout ? (
                <div className="mt-6 space-y-2">
                  <Button size="default" className="w-full" disabled>
                    {t('checkout.continueToPayment')}
                  </Button>
                  {!availabilityReady ? (
                    <p className="text-xs text-muted-foreground text-center">
                      {t('checkout.validatingCart')}
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground text-center">
                      {t('checkout.fixCartBeforePayment')}
                    </p>
                  )}
                </div>
              ) : (
                <Link href={`/${locale}/${storeId}/checkout`} className="block">
                  <Button size="default" className="w-full mt-6">
                    {t('checkout.continueToPayment')}
                  </Button>
                </Link>
              )}

              <Link href={`/${locale}/${storeId}/catalog`} className="block mt-4">
                <Button variant="outline" className="w-full">
                  {t('checkout.continueShopping')}
                </Button>
              </Link>

              <Button
                variant="ghost"
                className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={() => setShowClearCartDialog(true)}
              >
                {t('checkout.clearCart')}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      <CartRelatedProducts
        productIds={[...new Set(items.map((i) => i.productId))]}
        storeId={resolvedStoreId || storeId}
        locale={locale}
      />

      <Dialog open={showClearCartDialog} onOpenChange={setShowClearCartDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('checkout.confirmClearCart')}</DialogTitle>
            <DialogDescription>
              {t('checkout.confirmClearCartDescription')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClearCartDialog(false)}>
              {t('checkout.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                clearCart();
                setShowClearCartDialog(false);
                toast.success(t('checkout.cartCleared'));
              }}
            >
              {t('checkout.clearCart')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
